import React, { FC, useState } from 'react'
import { PrimeIcons } from 'primereact/api'
import { Column } from 'primereact/column'

import {
    ChargePoint,
    NfcTag,
    useCompanyGetChargePointsWithNfcInfoForTagQuery,
    useCompanySetChargePointsNfcInfoForTagMutation,
} from '../../../../redux/api/web-api'
import { Input } from '../../../../uikit/Input'
import useText from '../../../../hooks/useText'
import { Table } from '../../../../uikit/Table'
import { useAdaptConnectorNaming } from '../../../../../lib/adaptConnectorNaming'
import { Switch } from '../../../../uikit/Switch'
import { Dialog } from '../../../../uikit/Dialog'

type Props = {
    visible: boolean
    onHide: () => void
    rfidTag?: NfcTag
}

export const RfidTagChargePointsDialog: FC<Props> = ({ visible, onHide, rfidTag }) => {
    const text = useText()
    const { data: chargePoints, isLoading: chargePointsIsLoading } = useCompanyGetChargePointsWithNfcInfoForTagQuery(
        { companyId: rfidTag?.companyId || '', nfcTagId: rfidTag?.id || '' },
        { skip: !rfidTag?.companyId || !rfidTag?.id }
    )
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const [updateChargePointsNfcAccess] = useCompanySetChargePointsNfcInfoForTagMutation()

    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

    const updateNfcAccess = async (
        chargePointId: string | null | undefined,
        connectorId: number | undefined,
        hasNfcAccess?: boolean
    ) => {
        setLoadingStates((prev) => ({ ...prev, [`${chargePointId}_${connectorId}`]: true }))
        try {
            if (rfidTag) {
                await updateChargePointsNfcAccess({
                    body: [{ chargePointId, connectorId, hasNfcAccess }],
                    companyId: rfidTag.companyId!,
                    nfcTagId: rfidTag.id!,
                }).unwrap()
            }
        } catch (err) {
            // ignore
        } finally {
            setLoadingStates((prev) => ({ ...prev, [`${chargePointId}_${connectorId}`]: false }))
        }
    }
    const [globalFilter, setGlobalFilter] = useState('')

    const resetDialogState = () => {
        setGlobalFilter('')
        setLoadingStates({})
    }

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            onShow={resetDialogState}
            header={text('lblRFIDAccessToChargers')}
            style={{ width: '50rem' }}
        >
            <div className="flex flex-wrap align-items-center justify-content-end mb-3 pt-1">
                <Input
                    id="charge-points-search-input"
                    value={globalFilter}
                    type="search"
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={text('lblSearch')}
                    icon={PrimeIcons.SEARCH}
                />
            </div>

            <Table
                value={chargePoints}
                loading={chargePointsIsLoading}
                paginator
                scrollable
                scrollHeight="30rem"
                globalFilter={globalFilter}
                emptyMessage={text('msgNoChargePointsFound')}
                tableClassName="min-w-full"
            >
                <Column field="id" header={text('lblId')} />
                <Column field="name" header={text('lblName')} />
                <Column
                    className="w-4"
                    body={(chargePoint: ChargePoint) => (
                        <div className="flex flex-column gap-2">
                            {chargePoint.connectors
                                ?.filter((connector) => connector.connectorFunctionType === 'Charger')
                                .map((connector) => (
                                    <Switch
                                        key={connector.connectorId}
                                        checked={!!connector.hasNfcAccess}
                                        onChange={(event) =>
                                            void updateNfcAccess(chargePoint.id, connector.connectorId, event.value)
                                        }
                                        loading={loadingStates[`${chargePoint.id}_${connector.connectorId}`]}
                                        label={adaptConnectorNaming(connector.name)}
                                        inputId={`switch-${chargePoint.id}-${connector.connectorId}`}
                                        pt={{
                                            input: {
                                                'data-testid': `switch-${chargePoint.id}-${connector.connectorId}`,
                                            },
                                        }}
                                    />
                                ))}
                        </div>
                    )}
                    header={text('lblRFIDAccess')}
                />
            </Table>
        </Dialog>
    )
}
